/**
 * V70 Phase C — 대진표 FE 공용 타입 (Admin + 참가자 공유)
 */
export type BracketStatus =
  | 'draft' | 'drawn' | 'published' | 'in-progress' | 'completed' | 'cancelled';
export type MatchStatus = 'pending' | 'ready' | 'in-progress' | 'completed' | 'bye';
export type Side = 'A' | 'B';
export type BestOfMode = 1 | 3 | 5;

export interface TsLike { toMillis?: () => number; _seconds?: number; seconds?: number }

export interface PublicParticipantView {
  publicParticipantId: string;
  displayName: string;
  teamName: string | null;
  orgUnitName: string | null;
  mode: 'solo' | 'team';
  memberCount: number;
}

export interface RoundConfig { roundNo: number; bestOf: BestOfMode; label: string | null }

export interface PublicBracketView {
  bracketId: string;
  sourceFormId: string;
  sourceFormTitle: string;
  type: 'single-elimination';
  status: BracketStatus;
  startRoundSize: number;
  totalRounds: number;
  roundConfig: RoundConfig[];
  participants: PublicParticipantView[];
  participantCount: number;
  byeCount: number;
  slotAnnouncementOrder: Array<string | null>;
  publishedAt: TsLike;
  championPublicParticipantId: string | null;
  cancelReason?: string | null;
}

// V70 QA 신규 기능 #1·#3 (2026-10-22)
export interface MatchScheduleView {
  kind: 'single' | 'range';
  startDate: string; // YYYY-MM-DD
  endDate: string | null;
}
export interface MatchBroadcastView {
  enabled: boolean;
  startAt: TsLike;
  endAt: TsLike;
  location: string;
}

export interface PublicMatchView {
  matchId: string;
  bracketId: string;
  roundNo: number;
  slotIndex: number;
  bestOf: BestOfMode;
  playerA: { publicParticipantId: string | null; sourceMatchId: string | null };
  playerB: { publicParticipantId: string | null; sourceMatchId: string | null };
  sets: Array<{ setNo: number; winnerSide: Side; recordedAt: TsLike }>;
  status: MatchStatus;
  winnerSide: Side | null;
  completedAt: TsLike | null;
  nextMatchId: string | null;
  nextSlotSide: Side | null;
  schedule?: MatchScheduleView | null;
  broadcast?: MatchBroadcastView | null;
}

export function roundLabelOf(cfg: RoundConfig, totalRounds: number): string {
  if (cfg.label) return cfg.label;
  const remaining = totalRounds - cfg.roundNo + 1;
  // V70 QA 추가 수정 #7 (2026-10-21) — N강 라벨 정합.
  //   remaining 은 "현재 라운드부터 결승까지 남은 라운드 수".
  //   size = 2^remaining 이 현재 라운드에서 겨루는 참가자 수.
  //   예) 8팀 토너먼트 (totalRounds=3): round1 → remaining=3 → size=8 → "8강",
  //                                     round2 → remaining=2 → size=4 → "4강" (= 준결승),
  //                                     round3 → remaining=1 → size=2 → "결승".
  //   이전엔 `remaining === 3 → '4강'` 하드코딩되어 8강 라운드가 "4강" 으로 노출되는 버그.
  if (remaining === 1) return '결승';
  if (remaining === 2) return '준결승';
  const size = Math.pow(2, remaining);
  return `${size}강`;
}

export function tsToMs(ts: TsLike | null | undefined): number {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts._seconds === 'number') return ts._seconds * 1000;
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return 0;
}

export function statusLabel(s: BracketStatus): string {
  switch (s) {
    case 'draft': return '룰 작성 중';
    case 'drawn': return '추첨 완료 (미게시)';
    case 'published': return '게시됨';
    case 'in-progress': return '진행 중';
    case 'completed': return '종료 (우승자 확정)';
    case 'cancelled': return '취소됨';
  }
}

export function statusColor(s: BracketStatus): string {
  return ({
    draft: '#6b7280', drawn: '#2563eb',
    published: '#059669', 'in-progress': '#059669',
    completed: '#8b5cf6', cancelled: '#dc2626',
  } as const)[s];
}
